import { useEffect, useRef } from 'react';
import { type CoreThemeColors, deriveTokensFromCore, tokenToCssVar } from '@/lib/themeEngine';

interface ProfileThemeOverlayProps {
  colors: CoreThemeColors | null | undefined;
}

/**
 * Applies a profile theme's colors to the document root.
 * When unmounted, restores the original CSS custom properties.
 */
export function ProfileThemeOverlay({ colors }: ProfileThemeOverlayProps) {
  const originalVars = useRef<Map<string, string>>(new Map());
  const applied = useRef(false);

  useEffect(() => {
    if (!colors) return;

    const root = document.documentElement;
    const tokens = deriveTokensFromCore(colors.background, colors.text, colors.primary);

    // Save originals and apply new values
    for (const [key, value] of Object.entries(tokens)) {
      const cssVar = tokenToCssVar(key);
      if (!applied.current) {
        originalVars.current.set(cssVar, root.style.getPropertyValue(cssVar));
      }
      root.style.setProperty(cssVar, value);
    }
    applied.current = true;

    return () => {
      // Restore originals
      for (const [cssVar, original] of originalVars.current.entries()) {
        if (original) {
          root.style.setProperty(cssVar, original);
        } else {
          root.style.removeProperty(cssVar);
        }
      }
      originalVars.current.clear();
      applied.current = false;
    };
  }, [colors]);

  return null;
}
